/**
 * SearchPage — homepage (/) — WI-43 redesign.
 *
 * Layout (top to bottom):
 *   1. AppHeader (sticky, frosted glass, new wordmark)
 *   2. StickyFilterBar — shown when filter card is scrolled out of view + results exist
 *   3. .ors-shell (max-width 1080px centered):
 *      a. Hero — eyebrow + "Find your roster spot." + lede. No CTAs, no preview card.
 *      b. DivisionStrip — compact bordered pill row
 *      c. InlineFilterCard — sport grid → position groups → division chips →
 *                            region chips → state sub-chips → year chips →
 *                            live count strip footer
 *      d. Results section — empty state or result cards
 *      e. Ad slot (below results)
 *      f. Disclaimer
 *
 * Architecture changes from pre-WI-43:
 *   - FilterPanel, FilterModal, FilterBottomSheet: deleted.
 *   - DivisionTile: deleted.
 *   - InlineFilterCard: new component, always visible.
 *   - StickyFilterBar: new component, IntersectionObserver-based.
 *   - AppHeader: updated wordmark; SchoolLookupInput removed from homepage.
 *
 * Preserved behaviors:
 *   - WI-19: URL ↔ filter round-trip via useSearchParams
 *   - WI-19: scroll save/restore on back-nav
 *   - WI-19: auto-fetch on mount when URL has filters
 *   - WI-24: results cleared on interactive filter change
 *   - WI-38: state sub-filter inside the inline card
 *
 * Division multi-select (WI-43):
 *   SearchFilters.division is now string[] | undefined (was string | undefined).
 *   Serialized as comma-separated in URL (e.g. `division=ncaa_d1,ncaa_d2`).
 *   filtersFromSearchParams reads multiple values from the comma-separated param.
 *
 * Live count strip:
 *   When sport+position are set, fetches /api/v1/schools/distribution with a
 *   400ms debounce. Builds 5 simple histogram buckets from the distribution buckets.
 *   Uses the existing fetchScoreDistribution() API call.
 *
 * DivisionStrip pill click:
 *   - First-time visitor (no usable localStorage filter): pre-selects division chip
 *     in InlineFilterCard, scrolls card into view, focuses first sport radio.
 *     No modal opens.
 *   - Returning visitor (sport + position + target_year in localStorage): fires search
 *     immediately with the composed filter. No modal opens.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchFilters, serializeFilters } from '../api/search';
import { useSearch } from '../hooks/useSearch';
import { useAuth } from '../hooks/useAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { WatchlistApiError } from '../api/watchlist';
import { ResultCard } from './ResultCard';
import { AuthPromptSheet } from './AuthPromptSheet';
import { AdSlot } from './AdSlot';
import { ErrorBanner } from './ui/ErrorBanner';
import { SkeletonRow } from './ui/SkeletonRow';
import { AppHeader } from './AppHeader';
import { readLastFilter, writeLastFilter } from '../util/lastFilter';
import {
  fetchSchoolCount,
  fetchScoreDistribution,
  SchoolCountResponse,
  DistributionBucket,
} from '../api/homepageApi';
import {
  InlineFilterCard,
  StickyFilterBar,
  DivisionStrip,
  DIVISIONS,
} from './InlineFilterCard';
import { TARGET_YEAR_OPTIONS } from '../constants/years';

// ── Scroll sessionStorage key prefix (WI-19) ─────────────────────────────────

const SCROLL_KEY_PREFIX = 'search_scroll_';

// ── Default target year ───────────────────────────────────────────────────────

function defaultTargetYear(): number {
  // AC-8: default is next-calendar-year chip (currentYear + 1).
  return TARGET_YEAR_OPTIONS[1] ?? new Date().getFullYear() + 1;
}

// ── filtersFromSearchParams (WI-19 + WI-43 division multi-select) ─────────────

function filtersFromSearchParams(params: URLSearchParams): SearchFilters {
  const positionParam = params.get('position');
  const initialPositions = positionParam
    ? positionParam.split(',').map((s) => s.trim()).filter(Boolean)
    : undefined;

  // WI-38: state codes
  const stateParam = params.get('state');
  const initialStates = stateParam
    ? stateParam.split(',').map((s) => s.trim()).filter(Boolean)
    : undefined;

  // WI-43: division multi-select — comma-separated
  const divisionParam = params.get('division');
  const initialDivisions = divisionParam
    ? divisionParam.split(',').map((s) => s.trim()).filter(Boolean)
    : undefined;

  const yearParam = params.get('target_year');

  return {
    sport: params.get('sport') ?? undefined,
    position: initialPositions && initialPositions.length > 0 ? initialPositions : undefined,
    division: initialDivisions && initialDivisions.length > 0 ? initialDivisions : undefined,
    region: params.get('region') ?? undefined,
    state: initialStates && initialStates.length > 0 ? initialStates : undefined,
    target_year: yearParam ? Number(yearParam) : defaultTargetYear(),
  };
}

// ── Simple 5-bucket histogram builder ────────────────────────────────────────
// Converts DistributionBucket[] from the API to the simple { range, count } format
// that SimpleHistogram in InlineFilterCard expects.

function buildSimpleBuckets(
  buckets: DistributionBucket[]
): { range: string; count: number }[] {
  if (buckets.length > 0) {
    return buckets.map((b) => ({ range: b.range, count: b.count }));
  }
  // Empty fallback
  return [
    { range: '0–20', count: 0 },
    { range: '20–40', count: 0 },
    { range: '40–60', count: 0 },
    { range: '60–80', count: 0 },
    { range: '80+', count: 0 },
  ];
}

// ── Division browse data shape ────────────────────────────────────────────────

const DIVISION_FALLBACK = DIVISIONS.map((d) => ({
  slug: d.slug,
  label: d.label,
  count: 0,
}));

// ── SearchPage ────────────────────────────────────────────────────────────────

export function SearchPage() {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;
  const { savedIds, save } = useWatchlist(isAuthenticated);

  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<SearchFilters>(() =>
    filtersFromSearchParams(searchParams)
  );

  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const pendingSaveRef = useRef<string | null>(null);
  const prevAuthRef = useRef(isAuthenticated);
  useEffect(() => {
    if (!prevAuthRef.current && isAuthenticated && pendingSaveRef.current) {
      const schoolId = pendingSaveRef.current;
      pendingSaveRef.current = null;
      save(schoolId).catch(() => {});
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated, save]);

  const { results, loading, error, resultCount, sort, setSort, submitSearch, clearResults } = useSearch();

  const mountedRef = useRef(false);
  useEffect(() => { mountedRef.current = true; }, []);

  const [resultsCleared, setResultsCleared] = useState(false);
  const resultsRenderedRef = useRef(false);
  const currentScrollKeyRef = useRef<string>('');
  const scrollRestoredRef = useRef(false);

  // ── School count data (division strip) ────────────────────────────────────

  const [schoolCountData, setSchoolCountData] = useState<SchoolCountResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSchoolCount().then((data) => {
      if (!cancelled) setSchoolCountData(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const divisionStripData = useMemo(() => {
    if (!schoolCountData) return DIVISION_FALLBACK;
    return DIVISIONS.map((d) => {
      const found = schoolCountData.by_division?.find((bd) => bd.division === d.slug);
      return { slug: d.slug, label: d.label, count: found?.count ?? 0 };
    });
  }, [schoolCountData]);

  // ── Live histogram state ──────────────────────────────────────────────────

  const [histLoading, setHistLoading] = useState(false);
  const [histDistribution, setHistDistribution] = useState<DistributionBucket[]>([]);
  const histDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasPreviewContext = Boolean(
    filters.sport && filters.position && filters.position.length > 0
  );

  useEffect(() => {
    if (!hasPreviewContext || !filters.sport) return;
    const firstPosition = filters.position?.[0];
    if (!firstPosition) return;

    if (histDebounceRef.current) clearTimeout(histDebounceRef.current);
    setHistLoading(true);

    histDebounceRef.current = setTimeout(() => {
      let cancelled = false;
      fetchScoreDistribution({
        sport: filters.sport!,
        position: firstPosition,
        division: filters.division?.[0], // pass first division for approximation
        target_year: filters.target_year,
      }).then((data) => {
        if (!cancelled) {
          setHistDistribution(data);
          setHistLoading(false);
        }
      }).catch(() => {
        if (!cancelled) setHistLoading(false);
      });
      return () => { cancelled = true; };
    }, 400);

    return () => {
      if (histDebounceRef.current) clearTimeout(histDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasPreviewContext,
    filters.sport,
    filters.position?.join(','),
    filters.division?.join(','),
    filters.target_year,
  ]);

  // Reset histogram when no preview context
  useEffect(() => {
    if (!hasPreviewContext) {
      setHistDistribution([]);
      setHistLoading(false);
    }
  }, [hasPreviewContext]);

  const histBuckets = useMemo(() => buildSimpleBuckets(histDistribution), [histDistribution]);
  const histCount = histDistribution.reduce((sum, b) => sum + b.count, 0);

  // ── Filter change handler (WI-24: clear results on interactive change) ────

  function handleFiltersChange(updated: SearchFilters) {
    setFilters(updated);
    if (!mountedRef.current) return;
    if (resultCount > 0 || resultsCleared) {
      clearResults();
      setResultsCleared(true);
    }
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  function handleSubmit(overrideFilter?: SearchFilters) {
    const f = overrideFilter ?? filters;
    setResultsCleared(false);
    const serialized = serializeFilters(f);
    setSearchParams(
      serialized ? Object.fromEntries(new URLSearchParams(serialized)) : {},
      { replace: true }
    );
    scrollRestoredRef.current = false;
    resultsRenderedRef.current = false;
    currentScrollKeyRef.current = serialized;
    submitSearch(f);
    writeLastFilter(f);
    // Scroll results section into view after a tick
    requestAnimationFrame(() => {
      const el = document.getElementById('results-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ── WI-19: Restore scroll on back-nav ─────────────────────────────────────

  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (resultCount === 0) return;
    const key = SCROLL_KEY_PREFIX + currentScrollKeyRef.current;
    const saved = sessionStorage.getItem(key);
    if (saved !== null) {
      const y = Number(saved);
      if (!Number.isNaN(y)) {
        window.scrollTo({ top: y });
        scrollRestoredRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultCount]);

  // WI-19: Save scroll on unmount
  useEffect(() => {
    return () => {
      const key = currentScrollKeyRef.current;
      if (key) sessionStorage.setItem(SCROLL_KEY_PREFIX + key, String(window.scrollY));
    };
  }, []);

  // WI-19: On mount with URL filters, auto-submit
  useEffect(() => {
    if (!filters.sport) return;
    currentScrollKeyRef.current = serializeFilters(filters);
    submitSearch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Save handlers ─────────────────────────────────────────────────────────

  async function handleSave(schoolId: string) {
    try {
      // WI-44 AC-6a: capture the active search filter's sport, year, and positions.
      await save(schoolId, {
        sportSlug: filters.sport,
        targetYear: filters.target_year,
        positionSlugs: filters.position && filters.position.length > 0
          ? filters.position
          : undefined,
      });
    } catch (err) {
      if (err instanceof WatchlistApiError && err.status === 401) {
        pendingSaveRef.current = schoolId;
        setAuthPromptOpen(true);
      }
    }
  }

  function handleAuthPrompt(schoolId?: string) {
    if (schoolId) pendingSaveRef.current = schoolId;
    setAuthPromptOpen(true);
  }

  // ── Division strip pill click ──────────────────────────────────────────────
  //
  // First-time visitor: pre-select division chip, scroll filter card into view.
  // Returning visitor: fire search immediately with composed filter.

  const filterCardRef = useRef<HTMLElement | null>(null);
  const firstRadioRef = useRef<HTMLButtonElement | null>(null);

  function handleDivisionPillClick(divisionSlug: string) {
    const stored = readLastFilter();
    const isUsable = Boolean(
      stored?.sport &&
      stored.position && stored.position.length > 0 &&
      stored.target_year
    );

    if (isUsable && stored) {
      // Returning visitor — apply division directly, fire search.
      const composed: SearchFilters = {
        sport: stored.sport,
        position: stored.position,
        division: [divisionSlug],
        region: stored.region,
        target_year: stored.target_year,
      };
      const composedKey = SCROLL_KEY_PREFIX + serializeFilters(composed);
      try { sessionStorage.removeItem(composedKey); } catch { /* non-essential */ }
      setFilters(composed);
      handleSubmit(composed);
    } else {
      // First-time visitor — pre-select division chip, scroll to filter card.
      setFilters((prev) => ({
        ...prev,
        division: [divisionSlug],
      }));
      requestAnimationFrame(() => {
        const el = document.getElementById('filter-form');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => firstRadioRef.current?.focus(), 350);
      });
    }
  }

  // ── Sticky bar (IntersectionObserver on the filter form) ──────────────────

  const [formInViewport, setFormInViewport] = useState(true);

  useEffect(() => {
    const el = document.getElementById('filter-form');
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry) setFormInViewport(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  function handleEditFilters() {
    const el = document.getElementById('filter-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => firstRadioRef.current?.focus(), 350);
  }

  // ── Derived state ─────────────────────────────────────────────────────────

  const hasResults = resultCount > 0;
  const stickyVisible = hasResults && !formInViewport;
  const resolvedAuthenticated = authLoading ? false : isAuthenticated;

  const currentYear = new Date().getFullYear();
  const eyebrowYearRange = `${currentYear}–${currentYear + 3}`;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--ors-bg)' }}>
      <AppHeader isAuthenticated={resolvedAuthenticated} watchlistCount={savedIds.size} />

      <StickyFilterBar
        visible={stickyVisible}
        filters={filters}
        resultCount={resultCount}
        onEdit={handleEditFilters}
      />

      <main className="ors-shell">

        {/* ══ HERO ═════════════════════════════════════════════════════ */}
        <section className="ors-hero">
          <div className="ors-hero-inner">
            <p className="ors-eyebrow">Recruiting · {eyebrowYearRange}</p>
            <h1 className="ors-h1">Find your roster spot.</h1>
            <p className="ors-lede">
              Real open positions from college programs. Filter by sport, position, and
              division — the data is below.
            </p>
          </div>
        </section>

        {/* ══ DIVISION BROWSE STRIP ════════════════════════════════════ */}
        <DivisionStrip
          divisions={divisionStripData}
          onPillClick={handleDivisionPillClick}
        />

        {/* ══ INLINE FILTER CARD ═══════════════════════════════════════ */}
        <div style={{ marginBottom: 36 }}>
          <InlineFilterCard
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onSubmit={() => handleSubmit()}
            firstRadioRef={firstRadioRef}
            histLoading={histLoading}
            histCount={histCount}
            histBuckets={histBuckets}
            hasResults={hasResults}
          />
        </div>

        {/* ══ RESULTS SECTION ══════════════════════════════════════════ */}
        <section id="results-section">

          {/* Sort bar — visible when results are present */}
          {hasResults && (
            <div className="flex items-center justify-between mb-3">
              <p
                className="text-sm font-medium tabular-nums"
                style={{ color: 'var(--ors-ink-2)' }}
                aria-live="polite"
                aria-atomic="true"
              >
                {loading
                  ? 'Searching…'
                  : `${resultCount} school${resultCount !== 1 ? 's' : ''} sorted by fit score`}
              </p>
              <div className="flex items-center gap-1.5 text-sm">
                <span style={{ color: 'var(--ors-muted)', marginRight: 4 }}>Sort by:</span>
                <button
                  type="button"
                  onClick={() => setSort('score')}
                  aria-pressed={sort === 'score'}
                  className="px-2 py-0.5 min-h-[44px] rounded focus:outline-none focus-visible:ring-2"
                  style={{
                    border: sort === 'score' ? '1px solid var(--ors-accent)' : 'none',
                    color: sort === 'score' ? 'var(--ors-accent)' : 'var(--ors-muted)',
                    fontWeight: sort === 'score' ? 600 : 400,
                  }}
                >
                  Score
                </button>
                <button
                  type="button"
                  onClick={() => setSort('name')}
                  aria-pressed={sort === 'name'}
                  className="px-2 py-0.5 min-h-[44px] rounded focus:outline-none focus-visible:ring-2"
                  style={{
                    border: sort === 'name' ? '1px solid var(--ors-accent)' : 'none',
                    color: sort === 'name' ? 'var(--ors-accent)' : 'var(--ors-muted)',
                    fontWeight: sort === 'name' ? 600 : 400,
                  }}
                >
                  A–Z
                </button>
              </div>
            </div>
          )}

          {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

          {loading && (
            <div aria-busy="true" aria-label="Loading results">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="mb-2"
                  style={{
                    height: 110,
                    borderRadius: 'var(--ors-radius-card)',
                    border: '1px solid var(--ors-line)',
                    background: 'var(--ors-surface)',
                    animation: 'ors-skel 1.4s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
          )}

          {/* ── Empty state branches (WI-24) ─────────────────────────── */}
          {!loading && !error && resultCount === 0 && resultsCleared && filters.sport && (
            <div className="text-center py-16">
              <p className="text-sm font-medium" style={{ color: 'var(--ors-ink-2)' }}>
                Adjust filters and tap Search.
              </p>
            </div>
          )}
          {!loading && !error && resultCount === 0 && !resultsCleared && filters.sport && (
            <div className="flex flex-col items-center text-center py-12">
              <p className="text-base font-semibold mb-2" style={{ color: 'var(--ors-ink)' }}>No matches right now.</p>
              <p className="text-sm mb-4" style={{ color: 'var(--ors-muted)' }}>Try widening your region or division.</p>
              <button
                type="button"
                onClick={() => setFilters({ target_year: filters.target_year })}
                className="text-sm focus:outline-none focus-visible:underline"
                style={{ color: 'var(--ors-accent)' }}
              >
                Clear filters
              </button>
            </div>
          )}
          {!loading && !error && resultCount === 0 && !filters.sport && (
            <div className="ors-results-empty">
              <div className="ors-empty-card">
                <div className="ors-empty-eyebrow">Results</div>
                <h3 className="ors-empty-title">No search yet</h3>
                <p className="ors-empty-body">
                  Your school list will appear here once you hit <strong>Show schools</strong>.
                  Try Men&apos;s Soccer · CB to get started.
                </p>
              </div>
            </div>
          )}

          {/* Results list */}
          {!loading && resultCount > 0 && (
            <ul className="divide-y" aria-label={`${resultCount} school results`} style={{ borderColor: 'var(--ors-line)' }}>
              {results.map((result) => (
                <li key={result.school_id}>
                  <ResultCard
                    result={result}
                    isAuthenticated={resolvedAuthenticated}
                    isSaved={savedIds.has(result.school_id)}
                    onSave={handleSave}
                    onAuthPrompt={() => handleAuthPrompt(result.school_id)}
                    sport={filters.sport}
                    targetYear={filters.target_year}
                    position={filters.position}
                    division={filters.division?.[0]}
                    region={filters.region}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ══ AD SLOT (below results) ═══════════════════════════════════ */}
        <div className="hidden md:block mt-8">
          <div
            className="text-[11px] font-bold tracking-[0.12em] uppercase mb-2"
            style={{ color: 'var(--ors-muted)' }}
          >
            Sponsored
          </div>
          <AdSlot variant="leaderboard" slotId={process.env['REACT_APP_AD_SLOT_SEARCH']} />
        </div>

        {/* ══ DISCLAIMER ═══════════════════════════════════════════════ */}
        <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--ors-line)' }}>
          <p className="text-[11.5px] leading-relaxed" style={{ color: 'var(--ors-muted)' }}>
            We don&apos;t predict offers — we surface where roster spots are opening up.
            Recruiting decisions are still up to coaches.
          </p>
        </div>

      </main>

      {/* ══ AUTH PROMPT ══════════════════════════════════════════════════ */}
      <AuthPromptSheet
        isOpen={authPromptOpen}
        onClose={() => setAuthPromptOpen(false)}
      />

      {/* ── Skeleton animation (SSR-friendly keyframe) ─────────────── */}
      <style>{`
        @keyframes ors-skel {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
